import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import moment from "moment";
import ModalContainer from "../modal/ModalContainer";
import {
    DialogContent,
    DialogActions,
    Button,
    Grid,
    DialogContentText,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import RichTextEditor from "../richTextEditor/RichTextEditor";
import Loading from "../loadingSpinner/Loading";
import { BookingStatusType } from "../../types/enum";

const DRAFT_STATUSES = [
    BookingStatusType.Draft.ordinal,
    BookingStatusType.Incomplete.ordinal
];

@inject("bookingRequestStore", "templateStore", "profileStore")
@observer
class BulkCancelModal extends Component {
    state = {
        cancellationReason: undefined,
        isLoading: false,
        cancelResults: null,
        submittedBookings: null
    };

    getDraftBookings = () => {
        const { selectedBookings } = this.props;
        if (!selectedBookings) return [];
        return selectedBookings.filter(b => DRAFT_STATUSES.includes(b.status));
    };

    getNonDraftBookings = () => {
        const { selectedBookings } = this.props;
        if (!selectedBookings) return [];
        return selectedBookings.filter(b => !DRAFT_STATUSES.includes(b.status));
    };

    handleCancelBookings = async () => {
        const { currentProfile } = this.props.profileStore;
        const { cancellationReason } = this.state;

        const draftBookings = this.getDraftBookings();
        const nonDraftBookings = this.getNonDraftBookings();

        if (draftBookings.length === 0 && nonDraftBookings.length === 0) {
            return;
        }

        const { selectedBookings } = this.props;
        this.setState({
            isLoading: true,
            submittedBookings: selectedBookings ? [...selectedBookings] : []
        });

        let cancelResult = { cancelled: [], failed: [] };
        let deleteResult = { deleted: [], failed: [] };

        if (nonDraftBookings.length > 0) {
            try {
                cancelResult = await this.props.bookingRequestStore.bulkCancelBookings(
                    nonDraftBookings.map(b => b.id),
                    cancellationReason,
                    currentProfile?.id
                );
            } catch (err) {
                const reason = err?.message || "Failed to cancel";
                cancelResult = {
                    cancelled: [],
                    failed: nonDraftBookings.map(b => ({ bookingId: b.id, reason }))
                };
            }
        }

        if (draftBookings.length > 0) {
            try {
                deleteResult = await this.props.bookingRequestStore.bulkDeleteBookings(
                    draftBookings.map(b => b.id),
                    currentProfile?.id
                );
            } catch (err) {
                const reason = err?.message || "Failed to delete";
                deleteResult = {
                    deleted: [],
                    failed: draftBookings.map(b => ({ bookingId: b.id, reason }))
                };
            }
        }

        this.setState({
            isLoading: false,
            cancelResults: {
                cancelled: cancelResult.cancelled || [],
                deleted: deleteResult.deleted || [],
                failed: [...(cancelResult.failed || []), ...(deleteResult.failed || [])]
            }
        });
    };

    handleCloseResults = () => {
        const { onClose, onComplete } = this.props;
        this.setState({ cancellationReason: undefined, cancelResults: null, submittedBookings: null });
        if (onComplete) {
            onComplete();
        }
        onClose();
    };

    handleCloseModal = () => {
        const { cancelResults } = this.state;
        if (cancelResults) {
            this.handleCloseResults();
            return;
        }
        this.setState({ cancellationReason: undefined, cancelResults: null });
        this.props.onClose();
    };

    getBookingInfo = (bookingId) => {
        const { submittedBookings } = this.state;
        const bookingsData = submittedBookings || [];
        // eslint-disable-next-line eqeqeq
        const booking = bookingsData.find(b => b.id == bookingId);
        if (booking) {
            return {
                date: booking.dateStart ? moment(booking.dateStart).format("DD/MM/YYYY") : "N/A",
                actName: booking.actName || booking.act?.actName || "N/A",
                venueName: booking.venueName || booking.venue?.profileName || "N/A"
            };
        }
        return { date: "N/A", actName: "N/A", venueName: "N/A" };
    };

    renderResults = () => {
        const { cancelResults } = this.state;
        if (!cancelResults) return null;

        const { cancelled, deleted, failed } = cancelResults;
        const succeededCount = cancelled.length + deleted.length;
        const hasFailures = failed && failed.length > 0;
        const allFailed = succeededCount === 0 && hasFailures;

        return (
            <DialogContent>
                <div style={{ textAlign: "center", padding: "16px 0", marginBottom: 16 }}>
                    {allFailed ? (
                        <>
                            <WarningIcon style={{ fontSize: 48, color: "#c62828", marginBottom: 8 }} />
                            <Typography variant="h6" style={{ color: "#c62828" }}>
                                Cancellation Failed
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                None of the bookings could be processed
                            </Typography>
                        </>
                    ) : hasFailures ? (
                        <>
                            <InfoIcon style={{ fontSize: 48, color: "#f57c00", marginBottom: 8 }} />
                            <Typography variant="h6" style={{ color: "#f57c00" }}>
                                Partially Cancelled
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {succeededCount} booking{succeededCount !== 1 ? "s" : ""} processed, {failed.length} failed
                            </Typography>
                        </>
                    ) : (
                        <>
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                backgroundColor: "#e8f5e9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 8px"
                            }}>
                                <span style={{ fontSize: 24, color: "#2e7d32" }}>&#10003;</span>
                            </div>
                            <Typography variant="h6" style={{ color: "#2e7d32" }}>
                                Successfully Cancelled
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {succeededCount} booking{succeededCount !== 1 ? "s" : ""} processed successfully
                            </Typography>
                        </>
                    )}
                </div>

                {hasFailures && (
                    <Paper elevation={1} style={{ padding: 16, marginBottom: 16, backgroundColor: "#fff3e0" }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: "#e65100" }}>
                            Failed ({failed.length})
                        </Typography>
                        <div style={{ maxHeight: 200, overflow: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Act</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {failed.map((item, index) => {
                                        const info = this.getBookingInfo(item.bookingId);
                                        return (
                                            <TableRow key={item.bookingId || index}>
                                                <TableCell>{info.date}</TableCell>
                                                <TableCell>{info.actName}</TableCell>
                                                <TableCell style={{ color: "#e65100" }}>{item.reason}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </Paper>
                )}

                {succeededCount > 0 && (
                    <Paper elevation={1} style={{ padding: 16, backgroundColor: "#e8f5e9" }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: "#2e7d32" }}>
                            Successfully Processed ({succeededCount})
                        </Typography>
                        <div style={{ maxHeight: 150, overflow: "auto" }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Act</TableCell>
                                        <TableCell>Venue</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cancelled.map((bookingId, index) => {
                                        const info = this.getBookingInfo(bookingId);
                                        return (
                                            <TableRow key={bookingId || index}>
                                                <TableCell>{info.date}</TableCell>
                                                <TableCell>{info.actName}</TableCell>
                                                <TableCell>{info.venueName}</TableCell>
                                                <TableCell style={{ color: "#2e7d32" }}>Cancelled</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {deleted.map((bookingId, index) => {
                                        const info = this.getBookingInfo(bookingId);
                                        return (
                                            <TableRow key={`del-${bookingId || index}`}>
                                                <TableCell>{info.date}</TableCell>
                                                <TableCell>{info.actName}</TableCell>
                                                <TableCell>{info.venueName}</TableCell>
                                                <TableCell style={{ color: "#1565c0" }}>Deleted</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </Paper>
                )}
            </DialogContent>
        );
    };

    render() {
        const { open, selectedBookings } = this.props;
        const { cancellationReason, isLoading, cancelResults } = this.state;
        const count = selectedBookings ? selectedBookings.length : 0;
        const draftCount = this.getDraftBookings().length;
        const cancelCount = this.getNonDraftBookings().length;

        const hasBoth = draftCount > 0 && cancelCount > 0;
        const onlyDrafts = draftCount > 0 && cancelCount === 0;

        let title;
        if (cancelResults) {
            title = "Cancellation Results";
        } else if (onlyDrafts) {
            title = `Delete ${draftCount} Draft${draftCount !== 1 ? "s" : ""} Confirmation`;
        } else if (hasBoth) {
            title = `Cancel/Delete ${count} Booking${count !== 1 ? "s" : ""} Confirmation`;
        } else {
            title = `Cancel ${cancelCount} Booking${cancelCount !== 1 ? "s" : ""} Confirmation`;
        }

        return (
            <ModalContainer
                open={open}
                onClose={this.handleCloseModal}
                title={title}
                disableBackdropClick={!!cancelResults}
                disableEscapeKeyDown={!!cancelResults}
            >
                <Loading showed={isLoading} />

                {cancelResults ? (
                    <>
                        {this.renderResults()}
                        <DialogActions>
                            <Button variant="contained" color="primary" onClick={this.handleCloseResults}>
                                Close
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent>
                            <Grid container spacing={8}>
                                {hasBoth && (
                                    <Grid item xs={12}>
                                        <DialogContentText>
                                            {cancelCount} booking(s) will be cancelled and {draftCount} draft(s) will be deleted.
                                        </DialogContentText>
                                    </Grid>
                                )}
                                {onlyDrafts && (
                                    <Grid item xs={12}>
                                        <DialogContentText>
                                            This cannot be undone. Would you like to delete {draftCount} draft booking(s)?
                                        </DialogContentText>
                                    </Grid>
                                )}
                                {cancelCount > 0 && (
                                    <Grid item xs={12} className="grid-cancel-modal">
                                        <RichTextEditor
                                            value={cancellationReason}
                                            placeholder="Enter your cancellation reason"
                                            onChange={value => this.setState({ cancellationReason: value })}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="outlined" onClick={this.handleCloseModal}>
                                Close
                            </Button>
                            <Button
                                id="btn-confirm-bulk-cancel-booking"
                                variant="contained"
                                color="primary"
                                onClick={this.handleCancelBookings}
                                disabled={count === 0}
                            >
                                {onlyDrafts ? "Delete" : "Cancel Booking"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </ModalContainer>
        );
    }
}

export default BulkCancelModal;
